import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, PenLine, ShieldCheck, X, FileText, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SignatureCanvas({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0033CC";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return [(t.clientX - r.left) * (ref.current.width / r.width),
            (t.clientY - r.top) * (ref.current.height / r.height)];
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(...pos(e));
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(...pos(e));
    ctx.stroke();
    dirty.current = true;
    setHasInk(true);
    onChange(ref.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    setHasInk(false);
    onChange("");
  };

  return (
    <div>
      <div className="relative">
        <canvas
          ref={ref} width={560} height={160}
          data-testid="signature-canvas"
          className="w-full h-40 rounded-xl bg-white touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasInk && (
          <span className="absolute inset-0 flex items-center justify-center text-zinc-400 text-sm pointer-events-none select-none">
            ✍ Nacrtajte potpis ovdje
          </span>
        )}
      </div>
      <button type="button" onClick={clear} data-testid="signature-clear"
        className="mt-2 text-xs text-zinc-500 hover:text-white flex items-center gap-1">
        <X className="w-3 h-3" /> Obriši potpis
      </button>
    </div>
  );
}

export default function ContractSign({ kind = "b2b" }) {
  const { token } = useParams();
  const apiBase = kind === "supplier" ? "supplier-contract" : "contract";
  const heading = kind === "supplier"
    ? "Ugovor o agencijskom posredovanju i poslovnoj suradnji"
    : "Ugovor o poslovnoj suradnji";
  const [state, setState] = useState("loading"); // loading | error | ready | done
  const [errMsg, setErrMsg] = useState("");
  const [info, setInfo] = useState(null);
  const pdfUrl = `${API}/${apiBase}/sign/${token}/pdf`;
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/${apiBase}/sign/${token}`);
        setInfo(data);
        if (data.already_signed) { setState("done"); return; }
        setName(data.contact_name || "");
        setState("ready");
      } catch (e) {
        setErrMsg(e?.response?.data?.detail || "Poveznica nije valjana.");
        setState("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Unesite ime i prezime potpisnika."); return; }
    if (!signature) { toast.error("Nacrtajte vlastoručni potpis."); return; }
    if (!consent) { toast.error("Morate potvrditi suglasnost s uvjetima ugovora."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/${apiBase}/sign/${token}`, {
        signatory_name: name.trim(),
        signatory_title: title.trim(),
        signature_png: signature,
        consent: true,
      });
      setState("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Potpisivanje nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-1">Juke Transfers · B2B</p>
        <h1 className="font-display text-2xl sm:text-3xl font-light mb-6">{heading}</h1>

        {state === "loading" && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="contract-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Učitavanje ugovora…
          </div>
        )}

        {state === "error" && (
          <div data-testid="contract-error" className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-200">
            {errMsg}
          </div>
        )}

        {state === "done" && (
          <div data-testid="contract-signed-success" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-8 text-center">
            <ShieldCheck className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
            <p className="text-lg text-emerald-200">Ugovor je uspješno potpisan</p>
            <p className="text-sm text-zinc-400 mt-2">
              Potpisani primjerak poslan je e-mailom {info?.company_name ? `tvrtki ${info.company_name}` : "vama"} i Juke Transfersu.
            </p>
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="signed-pdf-download"
              className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
              <Download className="w-4 h-4" /> Preuzmi potpisani ugovor (PDF)
            </a>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="rounded-2xl border border-white/10 overflow-hidden mb-6 bg-black/40">
              <div className="px-4 py-2.5 border-b border-white/10 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
                <FileText className="w-3.5 h-3.5 text-[#C5A059]" />
                <span className="flex-1 min-w-[180px]">Pregled ugovora — {info?.company_name} · OIB {info?.oib || "—"}</span>
                <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="contract-open-pdf-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-4 rounded-full bg-[#C5A059] text-black text-[10px] uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors">
                  <ExternalLink className="w-3 h-3" /> Otvori PDF
                </a>
              </div>
              <iframe title="Ugovor" src={pdfUrl} data-testid="contract-preview-frame"
                className="w-full h-[480px] bg-white" />
              <div className="px-4 py-2 text-[11px] text-zinc-500 border-t border-white/10">
                Ne vidite dokument? <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-[#C5A059] underline" data-testid="contract-open-pdf-fallback">Otvorite PDF u novoj kartici</a>.
              </div>
            </div>

            <div className="rounded-2xl border border-[#C5A059]/30 bg-[#C5A059]/[0.05] p-6 space-y-5">
              <div className="flex items-center gap-2">
                <PenLine className="w-4 h-4 text-[#C5A059]" />
                <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-300">Elektronički potpis</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">Ime i prezime potpisnika *</label>
                  <input data-testid="signatory-name" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm focus:border-[#C5A059] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">Funkcija / uloga</label>
                  <input data-testid="signatory-title" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="npr. Direktor, Voditelj recepcije"
                    className="w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm focus:border-[#C5A059] outline-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">Vlastoručni potpis (nacrtajte mišem ili prstom)</label>
                <SignatureCanvas onChange={setSignature} />
              </div>
              <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
                <input type="checkbox" data-testid="consent-checkbox" checked={consent}
                  onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#C5A059]" />
                <span>Potvrđujem da sam ovlašten/a zastupati tvrtku {info?.company_name} te da prihvaćam sve uvjete ovog Ugovora o poslovnoj suradnji.</span>
              </label>
              <button onClick={submit} disabled={busy} data-testid="sign-submit-btn"
                className="w-full h-12 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-sm uppercase tracking-[0.2em] font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                Potpiši ugovor
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
